import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { onValue, ref, push, update, remove } from "firebase/database";
import { db } from "../../../configs/Firebase";
import Fuse from "fuse.js";
import style from "./ManageLabels.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const ManageLabels = ({ show, onClose, inline = false }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [labels, setLabels] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [newLabelValue, setNewLabelValue] = useState("");
  const [newColor, setNewColor] = useState("--labelColor1");
  const [error, setError] = useState("");

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      const labelsRef = ref(db, `userData/${org.payload}/labels`);

      onValue(labelsRef, (snapshot) => {
        const data = snapshot.val();
        const temp = [];
        if (data) {
          for (let i in data) {
            temp.push({
              id: i,
              ...data[i],
            });
          }
        }
        setLabels(temp);
      });
    }
  }, [org]);

  const fuse = new Fuse(labels, {
    keys: [{ name: "value" }],
    includeMatches: true,
  });
  const [labelResults, setLabelResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  function searchHandler(e) {
    setError("");
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setLabelResults(fuse.search(value));
    } else {
      setLabelResults([]);
    }
  }

  const [colorOptions, setColorOptions] = useState([
    { value: "--labelColor1", selected: true },
    { value: "--labelColor2", selected: false },
    { value: "--labelColor3", selected: false },
    { value: "--labelColor4", selected: false },
    { value: "--labelColor5", selected: false },
    { value: "--labelColor6", selected: false },
    { value: "--labelColor7", selected: false },
    { value: "--labelColor8", selected: false },
  ]);

  function setNewColorHandler(index) {
    let tempColorOptions = [...colorOptions];
    for (let i in tempColorOptions) {
      tempColorOptions[i].selected = false;
    }
    tempColorOptions[index].selected = true;
    setNewColor(tempColorOptions[index].value);
    setColorOptions(tempColorOptions);
  }

  function createNew() {
    if (!newLabelValue.trim()) {
      setError("Label value cannot be empty");
      return;
    }
    
    // Check if label already exists
    if (labels.some(l => l.value.toLowerCase() === newLabelValue.trim().toLowerCase())) {
      setError("This label already exists");
      return;
    }

    if (org.payload) {
      const labelsRef = ref(db, `userData/${org.payload}/labels`);
      const newLabel = {
        value: newLabelValue.trim(),
        color: newColor,
        createdAt: { ".sv": "timestamp" }
      };
      push(labelsRef, newLabel);
      setNewLabelValue("");
      setCreatingNew(false);
      setError("");
      // Reset color to first option
      const resetColorOptions = colorOptions.map((opt, idx) => ({
        ...opt,
        selected: idx === 0
      }));
      setColorOptions(resetColorOptions);
      setNewColor("--labelColor1");
    }
  }

  function startEdit(label) {
    setEditingLabel(label);
    setNewLabelValue(label.value);
    setNewColor(label.color || "--labelColor1");
    setCreatingNew(false);
    setError("");
    // Update color options to show selected color
    const updatedColorOptions = colorOptions.map(opt => ({
      ...opt,
      selected: opt.value === (label.color || "--labelColor1")
    }));
    setColorOptions(updatedColorOptions);
  }

  function saveEdit() {
    if (!newLabelValue.trim()) {
      setError("Label value cannot be empty");
      return;
    }

    // Check if another label with this value exists
    if (labels.some(l => l.id !== editingLabel.id && l.value.toLowerCase() === newLabelValue.trim().toLowerCase())) {
      setError("This label value already exists");
      return;
    }

    if (org.payload && editingLabel) {
      const labelRef = ref(db, `userData/${org.payload}/labels/${editingLabel.id}`);
      update(labelRef, {
        value: newLabelValue.trim(),
        color: newColor
      });
      setEditingLabel(null);
      setNewLabelValue("");
      setNewColor("--labelColor1");
      setError("");
      // Reset color options
      const resetColorOptions = colorOptions.map((opt, idx) => ({
        ...opt,
        selected: idx === 0
      }));
      setColorOptions(resetColorOptions);
    }
  }

  function deleteLabel(labelId) {
    if (org.payload) {
      const labelRef = ref(db, `userData/${org.payload}/labels/${labelId}`);
      remove(labelRef);
    }
  }

  function cancel() {
    setCreatingNew(false);
    setEditingLabel(null);
    setNewLabelValue("");
    setNewColor("--labelColor1");
    setError("");
    // Reset color options
    const resetColorOptions = colorOptions.map((opt, idx) => ({
      ...opt,
      selected: idx === 0
    }));
    setColorOptions(resetColorOptions);
  }

  const displayLabels = searchTerm ? labelResults.map(r => r.item) : labels;

  return (
    <div className={style.manageLabelsContainer}>
      <div className={style.input}>
        <Input
          label="Search Labels"
          placeholder=""
          onChange={searchHandler}
          value={searchTerm}
          error={error}
        />
      </div>

      {(creatingNew || editingLabel) ? (
        <div className={style.editSection}>
          <Input
            label="Label Value"
            placeholder="Enter label value"
            onChange={(e) => setNewLabelValue(e.target.value)}
            value={newLabelValue}
            error={error}
          />
          <div className={style.colorPickerSection}>
            <label className={style.colorPickerLabel}>Color</label>
            <div className={style.colourPicker}>
              <ul>
                {colorOptions.map((color, index) => (
                  <li
                    key={index}
                    className={color.selected ? style.selected : ""}
                    style={{ "--bg": "var(" + color.value + ")" }}
                    onClick={() => setNewColorHandler(index)}
                  ></li>
                ))}
              </ul>
            </div>
          </div>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variants={["primary"]}
              onClick={editingLabel ? saveEdit : createNew}
            >
              {editingLabel ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ul className={style.labelList}>
            {displayLabels.length > 0 ? (
              displayLabels.map((label) => (
                <li key={label.id}>
                  <span 
                    className={style.labelName}
                    style={{ "--label-color": "var(" + (label.color || "--labelColor1") + ")" }}
                  >
                    {label.value}
                  </span>
                  <div className={style.labelActions}>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => startEdit(label)}
                    >
                      <Icon>edit</Icon>
                    </Button>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${label.value}"?`)) {
                          deleteLabel(label.id);
                        }
                      }}
                    >
                      <Icon>delete</Icon>
                    </Button>
                  </div>
                </li>
              ))
            ) : (
              <li className={style.noResults}>
                {searchTerm ? "No labels found" : "No labels created yet"}
              </li>
            )}
          </ul>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={() => setCreatingNew(true)}>
              Create New Label
            </Button>
            {!inline && (
              <Button variants={["text"]} onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManageLabels;

